import { reactive, computed } from 'vue'
import { useMakeAuthServiceInstance } from '@zoomcatalog/auth'
import type {
	Application,
	ValidatedEmail,
	AuthV1,
	RequestResetPassword,
	RequestSignup,
	SignupVerifyResend,
} from '@zoomcatalog/auth'
import {
	AssetVariation,
	CreateVirtualSamplesBatchResponse,
	FetchAssetImprintAreasResponse,
	FetchAssetsResponse,
	FetchAssetsSettingsResponse,
	VirtualSamplesByTaskIdFetchResponse,
	VirtualSamplesCreateResponse,
	VirtualSamplesEditResponse,
	VirtualSamplesFetchResponse,
	VirtualSampleDetails,
	useCreateAssetsServiceInstance,
	useCreateUnsplashInstance,
	useMakeVirtualsServiceInstance,
	LibraryAsset,
} from '@zoomcatalog/design-system'
import {
	FetchProductAttributesResponse,
	FetchProductByIdResponse,
	FetchProductsResponse,
	RawProductVariation,
	useCreateProductLibraryServiceInstance,
} from '@zoomcatalog/design-templates'
import { useDesignSettingsStore } from '@/composable/StoreUtils/useDesignSettingsStore'
import { useI18n } from '@/composable/useI18n'
import api from '@/config/axios'
import {
	GET_ASSET_LIBRARY_SEARCHABLE,
	GET_ASSET_LIBRARY,
	PRODUCTS_GET_BY_KEYWORD,
	PRODUCT_GET_BY_ID,
	GET_PRODUCT_ATTRIBUTES_LIST,
} from '@/config/endpoints'
import unsplashApi from '@/config/unsplash'
import { translate } from '@/i18n'
import { useSelector } from '@/store/rootStore'

export function makeAuthServiceOptions() {
	return {
		async refreshToken(refreshToken: string) {
			const endpoints = useSelector((state) => state.sniffer.endpoints)

			return api
				.post<{ data: AuthV1 }>(
					'/auth/refresh-token',
					{
						client_id: import.meta.env.VITE_CRM_CLIENT_ID,
						refresh_token: refreshToken,
					},
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => response.data.data)
				.catch((error) => {
					error.customMessage = translate('errors.UNABLE_TO_REFRESH_TOKEN')
					throw error
				})
		},
	}
}

export function useSetupServices() {
	const { t } = useI18n()
	const authServiceOptions = makeAuthServiceOptions()
	const endpoints = useSelector((state) => state.sniffer.endpoints)

	const companyId = useSelector((state) => state.auth.company?.company_id ?? '')

	const failSomeRequestsTesting = window.location.hash.includes(
		'ServicesFailSomeRequestsTesting'
	)
	const urlFailTestingSuffix = failSomeRequestsTesting ? '/unknown-url' : ''

	const { settings } = useDesignSettingsStore()

	const plInstance = useCreateProductLibraryServiceInstance(
		reactive({
			currency: computed(() => settings.value.currency) as unknown as string,
			async getProductsQuery({ query, currency, limit, offset, filters, sort }) {
				const { endpoint } = PRODUCTS_GET_BY_KEYWORD

				return api
					.get<FetchProductsResponse>(endpoint(), {
						params: { q: query, currency, limit, offset, filters, sort },
					})
					.then((data) => data.data)
					.catch((error) => {
						error.customMessage = t('errors.UNABLE_TO_LOAD_PRODUCTS')
						throw error
					})
			},
			async getProductByIdQuery({ productId, partId, filters }) {
				const { endpoint } = PRODUCT_GET_BY_ID

				return api
					.get<{ contents: FetchProductByIdResponse }>(
						endpoint(productId) + urlFailTestingSuffix,
						{
							params: {
								cy_part_id: partId,
								...(filters?.length ? { filters: `[${filters?.join(',')}]` } : {}),
							},
						}
					)
					.then((data) => data.data.contents)
					.catch((error) => {
						error.customMessage = t('errors.UNABLE_TO_LOAD_PRODUCT_DETAILS')
						throw error
					})
			},
			async getProductByPartIdQuery({ productId, partId, filters }) {
				const { endpoint } = PRODUCT_GET_BY_ID

				return api
					.get<{ contents: RawProductVariation }>(
						endpoint(productId) + urlFailTestingSuffix,
						{
							params: {
								part_id: partId,
								...(filters?.length ? { filters: `[${filters?.join(',')}]` } : {}),
							},
						}
					)
					.then((data) => data.data.contents)
					.catch((error) => {
						error.customMessage = t('errors.UNABLE_TO_LOAD_PRODUCT_DETAILS')
						throw error
					})
			},
			async getProductAttributesQuery({ productAttribute, relatedTo }) {
				return api
					.get<FetchProductAttributesResponse>(
						GET_PRODUCT_ATTRIBUTES_LIST.endpoint(productAttribute ?? ''),
						{
							...(relatedTo ? { params: { related_to: relatedTo } } : {}),
						}
					)
					.then((data) => data.data)
					.catch((error) => {
						error.customMessage = t('errors.UNABLE_TO_LOAD_PRODUCT_ATTRIBUTES')
						throw error
					})
			},
		})
	)

	const assetsInstance = useCreateAssetsServiceInstance({
		async getAssetById(assetId, params) {
			return api
				.get<LibraryAsset>(`/asset/${assetId}`, {
					baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
					params,
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_ASSET')
					throw error
				})
		},
		async getPreSignedUrl(data) {
			let assets = [
				{
					is_for_company: false,
					filename: data.filename,
					is_public: 0,
					limited_to: 'owner',
				},
			]

			if (Array.isArray(data.filename)) {
				assets = data.filename.map((name) => ({
					is_for_company: false,
					filename: name,
					is_public: 0,
					limited_to: 'owner',
				}))
			}

			return api
				.post<{
					res: Array<{
						fields: {
							AWSAccessKeyId: string
							acl: string
							'content-type': string
							key: string
							policy: string
							signature: string
							success_action_status: string
						}
						url: string
						uuid: string
					}>
				}>(
					'/asset/upload',
					{
						assets,
						tags: data.tags,
						is_public: 0,
						limited_to: 'owner',
					},
					{
						baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
						headers: {
							'X-Zoom-Company': companyId.value,
						},
					}
				)
				.then((response) => response.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_PREPARE_UPLOAD')
					throw error
				})
		},
		getResizeUrl(
			assetId,
			[w, h],
			urlOptions?: { repositionResize?: boolean; fit?: string }
		) {
			return `${
				endpoints.value.ASSET_SERVICE_API_ENDPOINT
			}/asset/${assetId}/resize/${w}x${h ?? ''}${
				urlOptions?.fit ? '?fit=' + urlOptions.fit : ''
			}`
		},
		async getAssetsQuery(data) {
			const { endpoint } = GET_ASSET_LIBRARY

			return api
				.get<FetchAssetsResponse>(endpoint(data.tags), {
					baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
					headers: {
						'Cache-Control': 'max-age=0',
					},
					params: {
						limit: data.limit,
						sort: '-created',
						offset: data.offset,
						q: data.search,
						is_active: data.isActive,
						filters: decodeURIComponent(data.filters || ''),
					},
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_ASSETS')
					throw error
				})
		},
		async getAssetsQuerySearchable(data) {
			return api
				.get<FetchAssetsResponse>(GET_ASSET_LIBRARY_SEARCHABLE.endpoint, {
					baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
					headers: {
						'Cache-Control': 'max-age=0',
						'X-Zoom-Company': companyId.value,
					},
					params: {
						...(data.tags.some(Boolean) ? { tags: data.tags.join('+') } : {}),
						...(data.fields?.some(Boolean) ? { fields: data.fields.join(',') } : {}),
						limit: data.limit,
						offset: data.offset,
						q: data.search,
						filters: decodeURIComponent(data.filters || ''),
						sort: data.sort,
					},
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_ASSETS')
					throw error
				})
		},
		async uploadSingleAsset(assetData, formData) {
			return api
				.post(assetData.url, formData, {
					baseURL: undefined,
					headers: {
						'X-Zoom-Company': companyId.value,
					},
					transformRequest: (data, headers) => {
						if (headers) {
							// TODO: Check why headers.common is set to string instead of object
							delete (headers.common as unknown as Record<string, string>)
								.Authorization
						}
						return data
					},
				})
				.then(() => ({ data: assetData }))
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_UPLOAD_ASSET')
					throw error
				})
		},
		async updateAsset(assetId, data) {
			await api.patch(
				`${endpoints.value.ASSET_SERVICE_API_ENDPOINT}/asset/${assetId}`,
				data
			)
		},
		getOriginalUrl(assetId) {
			return `${endpoints.value.ASSET_SERVICE_API_ENDPOINT}/asset/${assetId}/original`
		},
		getUnwrappedS3Url(url: string) {
			return api
				.head<string>(url, {
					baseURL: undefined,
					transformRequest: (data, headers) => {
						if (headers) {
							delete (headers.common as unknown as Record<string, string>)
								.Authorization
						}
						return data
					},
				})
				.then((response) => {
					const finalURL = new URL(response.request.responseURL)
					return finalURL.href
				})
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_UNWRAPPED_S3_URL')
					throw error
				})
		},
		async getAssetImprintAreasQuery(assetId: string) {
			return api
				.get<FetchAssetImprintAreasResponse>(
					`/asset/${assetId}/imprint-areas?filters=is_active=true&sort=${[
						'-is_default',
						'is_verified',
						'name',
					].join(',')}`,
					{
						baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
					}
				)
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_IMPRINT_AREAS')
					throw error
				})
		},
		async getAssetSettingsQuery(assetId, data) {
			return api
				.get<FetchAssetsSettingsResponse>(`/asset/${assetId}/settings`, {
					baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
					headers: {
						'Cache-Control': 'max-age=0',
					},

					params: data,
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_ASSET_SETTINGS')
					throw error
				})
		},
		async getAssetVariations(assetId) {
			return api
				.get<AssetVariation[]>(`/asset/${assetId}/variations`, {
					baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
					headers: {
						'Cache-Control': 'max-age=0',
					},
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_ASSET_VARIATIONS')
					throw error
				})
		},
		async createAssetVariation(assetId, payload) {
			return api
				.post<{ variation_id: string }>(`/asset/${assetId}/variation`, payload, {
					baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_CREATE_ASSET_VARIATION')
					throw error
				})
		},
		async getAssetVariationById(assetId, variationId) {
			return api
				.get<AssetVariation>(`/asset/${assetId}/variation/${variationId}`, {
					baseURL: endpoints.value.ASSET_SERVICE_API_ENDPOINT,
					headers: {
						'Cache-Control': 'max-age=0',
					},
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_ASSET_VARIATION')
					throw error
				})
		},
		getTransformUrl({ assetId, size: [w, h], removeBackground, changeColor }) {
			const baseUrl = endpoints.value.ASSET_SERVICE_API_ENDPOINT

			const getChangeColorOnly = (color: string) => {
				return `${baseUrl}/asset/${assetId}/color/${color}`
			}
			const getRemoveBackgroundOnly = () => {
				return `${baseUrl}/asset/${assetId}/transparent`
			}

			const getRemoveContinuousOnly = () => {
				return `${baseUrl}/asset/${assetId}/transparent/continuous`
			}

			const getChangeColorRemoveBg = (color: string) => {
				return `${baseUrl}/asset/${assetId}/transparent/color/${color}`
			}

			const getChangeColorRemoveContinuous = (color: string) => {
				return `${baseUrl}/asset/${assetId}/transparent/continuous/color/${color}`
			}

			const getResizeImage = () => {
				return `${baseUrl}/asset/${assetId}/resize/${w}x${h ?? ''}`
			}

			function formImageUrl() {
				const image = changeColor
					? removeBackground
						? removeBackground === 'all-background'
							? getChangeColorRemoveBg(changeColor)
							: getChangeColorRemoveContinuous(changeColor)
						: getChangeColorOnly(changeColor)
					: removeBackground
					? removeBackground === 'all-background'
						? getRemoveBackgroundOnly()
						: getRemoveContinuousOnly()
					: getResizeImage()

				return image
			}

			return formImageUrl()
		},
	})

	const unsplashInstance = useCreateUnsplashInstance({
		async searchPhotos(data) {
			return unsplashApi.searchPhotos({
				query: data.query,
				page: data.page,
				perPage: data.perPage,
			})
		},

		async trackDownload(data) {
			return unsplashApi.trackDownload(data)
		},
	})

	const virtualsService = useMakeVirtualsServiceInstance({
		async getVirtual(id, ref) {
			return api
				.get<VirtualSampleDetails>(`/virtual/${id}?ref=${ref}`, {
					baseURL: endpoints.value.VIRTUAL_SAMPLES_API_ENDPOINT,
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_VIRTUAL_SAMPLE')
					throw error
				})
		},
		async getAccountVirtuals(options) {
			const retryAttempts = 3
			const retryDelayMs = 1000
			let lastError: any

			for (let attempt = 1; attempt <= retryAttempts; attempt++) {
				try {
					const data = await api.get<VirtualSamplesFetchResponse>(
						'/account/virtuals/published',
						{
							baseURL: endpoints.value.VIRTUAL_SAMPLES_API_ENDPOINT,
							params: {
								fields: ['images', 'layers_image_ref', 'product_image_ref'].join(','),
								...options,
								order_by: '-created_at',
							},
						}
					)

					return data.data
				} catch (error: any) {
					lastError = error

					if (attempt === retryAttempts) {
						lastError.customMessage = t('errors.UNABLE_TO_LOAD_VIRTUAL_SAMPLES')
						lastError.suppressNotification = true
						throw lastError
					}
					await new Promise((resolve) => setTimeout(resolve, retryDelayMs))
				}
			}

			throw lastError
		},
		async createAccountVirtual(payload) {
			return api
				.post<VirtualSamplesCreateResponse>('/virtual/publish', payload, {
					baseURL: endpoints.value.VIRTUAL_SAMPLES_API_ENDPOINT,
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_CREATE_VIRTUAL_SAMPLE')
					throw error
				})
		},
		async editAccountVirtual(payload) {
			return api
				.patch<VirtualSamplesEditResponse>('/virtual/publish', payload, {
					baseURL: endpoints.value.VIRTUAL_SAMPLES_API_ENDPOINT,
				})
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_EDIT_VIRTUAL_SAMPLE')
					throw error
				})
		},
		async createAccountVirtualsBatch(payload) {
			return api
				.post<CreateVirtualSamplesBatchResponse>(
					'/virtuals/publish' + urlFailTestingSuffix,
					payload,
					{
						baseURL: endpoints.value.VIRTUAL_SAMPLES_API_ENDPOINT,
					}
				)
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_CREATE_VIRTUAL_SAMPLES')
					throw error
				})
		},
		async getVirtualsByTaskId(options) {
			return api
				.get<VirtualSamplesByTaskIdFetchResponse>(
					`/virtuals/publish/${options.taskId}`,
					{
						params: {
							details: 'full',
						},
						baseURL: endpoints.value.VIRTUAL_SAMPLES_API_ENDPOINT,
					}
				)
				.then((data) => data.data)
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOAD_VIRTUAL_SAMPLES')
					throw error
				})
		},
	})

	const authService = useMakeAuthServiceInstance({
		async getApplication(subdomain: string) {
			return api
				.get<{ data: { application: Application } }>(
					`/subdomain/metadata?subdomain=${subdomain}`,
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => response.data.data.application)
				.catch((error) => {
					const isNewAuthWorkflow = useSelector(
						(state) => state.config.isNewAuthWorkflow
					)
					if (isNewAuthWorkflow.value) {
						error.customMessage = t('common.UNABLE_TO_LOAD_APP_DETAILS')
						throw error
					}
					return {} as Application
				})
		},

		async validateEmail(
			clientId: string,
			email: string,
			forceMigration: boolean
		) {
			const forceMigrationText = forceMigration ? 'true' : 'false'
			return api
				.post<{ data: ValidatedEmail }>(
					`/auth/presignin?lang=en-us&force_migration=${forceMigrationText}&email_format_check=true`,
					{
						client_id: clientId,
						email,
						force_migration: forceMigration,
					},
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => response.data.data)
				.catch((error) => {
					if (error.response?.status !== 404) {
						error.customMessage = t('errors.UNABLE_TO_VALIDATE_EMAIL')
						throw error
					}

					// this will override the error
					// and will make the request as a valid request
					const isNotFound =
						error.response?.data?.error?.code === 'USER_NOT_FOUND_MIGRATION'
					return {
						exists: !isNotFound,
						status: isNotFound ? 'not_found' : 'not_validated',
					}
				})
		},

		async login(clientId: string, email: string, password: string) {
			return api
				.post<any>(
					'/auth/signin?lang=en-us&v1_token=true&v2_token=true',
					{ client_id: clientId, email, password },
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => {
					const responseData = response.data.data

					return {
						long: responseData.tokens,
						short: responseData.v2_tokens,
					}
				})
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOGIN')
					throw error
				})
		},

		logout() {
			return api
				.get('/auth/signout', {
					baseURL: endpoints.value.SSO2_API_ENDPOINT,
				})
				.then(() => {
					// nothing to return here
					return {}
				})
				.catch((error) => {
					error.customMessage = t('errors.UNABLE_TO_LOGOUT')
					throw error
				})
		},

		refreshToken: authServiceOptions.refreshToken,

		async requestResetPassword(
			clientId: string,
			email: string,
			callbackUrl: string,
			errorCallback?: string,
			callbackVerify?: string
		) {
			return api
				.post<{ data: RequestResetPassword }>(
					'/auth/reset-password?lang=en-us&force_password_sync=true',
					{
						client_id: clientId,
						email,
						callback: callbackUrl + '?auth=reset-password',
						...(errorCallback && { callback_error: errorCallback }),
						...(callbackVerify && { callback_verify: callbackVerify }),
					},
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => response.data.data)
				.catch((error) => {
					error.customMessage =
						error.response?.data?.error?.message.replace(/\.$/, '') ||
						t('errors.UNABLE_TO_REQUEST_PASSWORD_RESET')
					throw error
				})
		},

		async resetPassword(clientId: string, token: string, password: string) {
			return api
				.post<any>(
					'/auth/reset-password/confirm?force_password_sync=true',
					{
						client_id: clientId,
						new_password: password,
						t: token,
					},
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => {
					const responseData = response.data.data

					return {
						long: responseData.tokens,
						short: responseData.v2_tokens,
					}
				})
				.catch((error) => {
					error.customMessage =
						error.response?.data?.error?.message.replace(/\.$/, '') ||
						t('errors.UNABLE_TO_RESET_PASSWORD')
					throw error
				})
		},

		async verifyResetPassword(token: string, code: string) {
			return api
				.post(
					'/auth/reset-password/verify',
					{
						t: token,
						code,
					},
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => response.data)
				.catch((error) => {
					error.customMessage =
						error.response?.data?.error?.message.replace(/\.$/, '') ||
						t('errors.UNABLE_TO_VERIFY_RESET_PASSWORD')
					throw error
				})
		},

		async signup(
			clientId: string,
			email: string,
			password: string,
			firstName: string,
			lastName: string,
			companyName: string,
			companyPhone: string,
			companyWebsite: string,
			role: string,
			newsAndUpdates: boolean,
			callbackUrl?: string
		) {
			return api
				.post<{ data: RequestSignup }>(
					'/auth/signup?force_synchronization=true&lang=en-us',
					{
						client_id: clientId,
						email,
						password,
						firstname: firstName,
						lastname: lastName,
						company_name: companyName,
						company_phone: companyPhone,
						company_website: companyWebsite,
						role,
						newsAndUpdates,
						callback: callbackUrl,
						contact_origin_url: window.location.hostname,
					},
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => {
					if (
						window.location.hostname.match(/\.local$/) ||
						window.location.hash.includes('AuthSignupCodes')
					) {
						// eslint-disable-next-line no-console
						console.log(
							'Codes',
							response.data.data?.email_meta?.message
								.match(/>(\d+)</g)
								?.map((m) => (m.length >= 8 ? m.replace(/[><]/g, '') : null))
								.filter((v) => v)
						)
					}

					return response.data.data
				})
				.catch((error) => {
					error.customMessage =
						error.response?.data?.error?.message.replace(/\.$/, '') ||
						t('errors.UNABLE_TO_SIGN_UP')
					throw error
				})
		},

		async signupVerifyResend(
			clientId: string,
			email: string,
			callbackUrl?: string
		) {
			return api
				.post<{ data: SignupVerifyResend }>(
					'/auth/signup/verify-resend?lang=en-us',
					{ client_id: clientId, email, callback: callbackUrl },
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => response.data.data)
				.catch((error) => {
					error.customMessage =
						error.response?.data?.error?.message.replace(/\.$/, '') ||
						t('errors.UNABLE_TO_RESEND_SIGNUP_VERIFICATION')
					throw error
				})
		},

		async signupVerify(clientId: string, email: string, code: string) {
			return api
				.post<any>(
					'/auth/signup/verify',
					{ client_id: clientId, email, otp_code: code },
					{
						baseURL: endpoints.value.SSO2_API_ENDPOINT,
					}
				)
				.then((response) => {
					const responseData = response.data.data

					return {
						long: responseData.tokens,
						short: responseData.v2_tokens,
					}
				})
				.catch((error) => {
					error.customMessage =
						error.response?.data?.error?.message.replace(/\.$/, '') ||
						t('errors.UNABLE_TO_VERIFY_SIGNUP')
					throw error
				})
		},
	})

	return {
		plInstance,
		virtualsService,
		assetsInstance,
		unsplashInstance,
		authService,
	}
}
